import { useState, useEffect, SyntheticEvent, useRef } from 'react';
import { PanelProvider } from '@/context/panel-context';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/20/solid';
import { Widget } from '@/components/widget';
import { useObjectForm } from '@/hooks/use-object-form';
import { useListener } from '@/hooks/use-listener';
import * as Dialog from '@radix-ui/react-dialog';
import clsx from 'clsx';
import { ChatBubbleOvalLeftEllipsisIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import type { GenerateResponseData } from '@/hooks/use-generate';
import { GenerateObjectForm } from '@/components/edit-widget/generate-object-form';
import { CreateNewObjectForm } from '@/components/edit-widget/create-new-object-form';
import { Header } from '@/components/edit-widget/header';
import { RefineForm } from '@/components/edit-widget/refine-form';
import { DeleteObject } from '@/components/edit-widget/delete-object';
import { ChainTriggerForm } from '@/components/edit-widget/chain-trigger-form';
import { EditForm } from '@/components/edit-widget/edit-form';
import './edit-widget.css';
import { useObjectExists } from '@/hooks/use-object-exists';
import { useDownloadMedia } from '@/hooks/use-download-media';
import { useUserDataclasses } from '@/hooks/user/use-user-dataclasses';
import { setNode } from '@tiptap/core/dist/commands';

export interface Headers {
  lastUpdateTimestamp: string;
  uuid: string;
}

interface Chain {
  value: string;
  label: string;
}

export function EditWidget({ id, type, initalCreateDataClass, defaultUuid, defaultExtraParams }: { id: string, type?: string, initalCreateDataClass?: string, defaultUuid?: string, defaultExtraParams?: Record<string, unknown> }) {
  const [title, setTitle] = useState('Edit Object');
  const [deleted, setDeleted] = useState<boolean>(false);
  const [objectSaved, setObjectSaved] = useState<boolean>(false);
  const [nodeFormData, setNodeFormData] = useState();
  const [uuids, setUuids] = useState<string[]>([]);
  const [containers, setContainers] = useState<string[]>([]);
  const [dataClassUuids, setDataClasses] = useState<string[]>([]);
  const [extraParams, setExtraParams] = useState<Record<string, unknown>>({});
  const [headers, setHeaders] = useState<Headers>({});
  const [chains, setChains] = useState<Chain[]>([]);
  const [create, setCreate] = useState<boolean>(false);
  const [createDataClass, setCreateDataclass] = useState<string>(
    'dataclass-general-image'
  );
  const [open, setOpen] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);
  const objectExists = useObjectExists();
  const downloadMedia = useDownloadMedia();
  const { data, refetch } = useObjectForm(
    uuids,
    containers,
    create,
    createDataClass,
    {
      onSuccess: (data: any) => {
        if (create) {
          setTitle('Create New ' + (data?.data?.resultset?.[0]?.structure?.title ?? ''));
        } else {
          setTitle('Edit Object');
        }
        setHeaders(data?.data?.resultset?.[0]?.headers ?? {});
        setChains(data?.data?.resultset?.[0]?.chains ?? []);

        const redirectToId = data?.data?.resultset?.[0]?.headers?.redirectTo;
        setSyncing(redirectToId === 'syncing' ? true : false);

        if (redirectToId && redirectToId !== 'syncing') {
          setSyncing(true);
          // check if node exists
          objectExists.mutateAsync({ uuid: redirectToId, container: containers[0] }).then((existsData) => {
            if (existsData.exists) {
              setUuids([redirectToId]);
            }
          });
        }
      },
    },
    extraParams
  );
  const { enabled } = useListener({
    id: id,
    listenerFn: (data) => {
      setUuids(data.map(({ uuid }: { uuid: string }) => uuid));
      setContainers(
        data.map(({ container }: { container: string }) => container)
      );
      setDataClasses(
        data.map(
          ({ dataClassUuid }: { dataClassUuid: string }) => dataClassUuid
        )
      );
      const { uuid: _u, container: _c, dataClassUuid: _d, root: _r, ...extras } = data[0] ?? {};
      setExtraParams(extras);
      setCreate(false);
      setOpen(false);
      setObjectSaved(false);
      refetch();
    },
    type: 'ObjectCollection',
  });
  const { data: dataClasses } = useUserDataclasses(type);

  const hasRun = useRef(false);
  const hasDefaultLoaded = useRef(false);

  useEffect(() => {
    if (!defaultUuid || hasDefaultLoaded.current) return;
    hasDefaultLoaded.current = true;
    setUuids([defaultUuid]);
    setContainers([type ?? 'vault']);
    setExtraParams(defaultExtraParams ?? {});
    setCreate(false);
  }, [defaultUuid, defaultExtraParams, type]);

  useEffect(() => {
    if (!dataClasses || hasRun.current) return;
    if (!initalCreateDataClass) return;

    hasRun.current = true;

    setCreate(true);
    setCreateDataclass(initalCreateDataClass);
    setDataClasses([initalCreateDataClass]);
    let containers = dataClasses?.dataClasses.filter((item) => item.value === initalCreateDataClass)
      .map((item) => item.container);
    setContainers(containers);
  }, [dataClasses, initalCreateDataClass]);


  useEffect(() => {
    if (!syncing) return;

    const interval = setInterval(() => {
      refetch();
    }, 10000);

    return () => clearInterval(interval);
  }, [syncing, refetch]);

  function handleUpsertStart(formData: any) {
    setNodeFormData(formData);
  }

  function handleUpsertSuccess(data) {
    if (initalCreateDataClass) {
      setCreate(true);
      setObjectSaved(true);
      setNodeFormData({});
    } else if (type === 'vault') {
      setObjectSaved(true);
    } else {
      setCreate(false);
      setHeaders(data);
      setUuids([data.uuid]);
      setObjectSaved(true);
    }
  }

  //receives data coming in
  const {
    structure: schema,
    data: formData,
    uiSchema,
  } = data?.data?.resultset?.[0] ?? {};

  useEffect(() => {
    if (!formData) {
      return;
    }
    setDeleted(false);
    setNodeFormData(formData);
    setOpen(false);
  }, [formData]);

  useEffect(() => {
    if (objectSaved === false) {
      return;
    }

    setTimeout(() => {
      setObjectSaved(false);
    }, 5000);
  }, [objectSaved]);

  const className = clsx(
    'block overflow-hidden text-ellipsis text-xs text-center p-3 rounded focus:ring focus:ring-offset-1',
    !open &&
      'bg-gray-200 dark:bg-gray-700 focus:ring-gray-100 dark:focus:ring-gray-600 text-gray-900 dark:text-gray-100',
    open &&
      'bg-indigo-100 dark:bg-indigo-900/20 focus:ring-indigo-500 dark:focus:ring-indigo-400 text-indigo-700 dark:text-indigo-300'
  );

  function handleGenerateSuccess(data: GenerateResponseData) {
    if (data.formData) {
      setNodeFormData(data.formData);
    }

    setOpen(false);
  }

  function handleRefineSuccess(data: any) {
    if (data.formData) {
      setOpen(false);
      setNodeFormData(data.formData);
    }
  }

  function handleDeleteSuccess() {
    setDeleted(true);
  }

  const handleCreateNewObject = (event: SyntheticEvent) => {
    event.preventDefault();

    const target = event.target as HTMLFormElement;
    const formData = new FormData(target);
    const dataClass = formData.get('dataclass[value]') as string | null;
    const container = type === 'vault'
      ? 'vault'
      : (formData.get('dataclass[container]') as string | null);

    if (!dataClass || !container) {
      return;
    }

    setCreateDataclass(dataClass);
    setCreate(true);
    setContainers([container]);
    setDataClasses([dataClass]);
    setUuids([]);
    setNodeFormData({});
    setObjectSaved(false);
    setOpen(false);
  };

  const handleDownloadClick = (event: SyntheticEvent) => {
    event.preventDefault();

    if (!uuids[0] || !containers[0]) {
      console.log('test');
      return;
    }

    downloadMedia.mutate({
      uuid: uuids[0],
      container: containers[0],
    });
  };

  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  return (
    <Widget id={id} title={title}>
      <div className="relative" ref={setContainer}>
          <PanelProvider>
            <div className="flex justify-between mb-4">
              {!deleted && !type && headers ? (
                <Header
                  initialChainStatus={
                    data?.data?.resultset?.[0]?.chainStatus ?? 'complete'
                  }
                  uuid={headers.uuid}
                  container={containers[0]}
                  timestamp={headers.timestamp}
                />
              ) : (
                <div></div>
              )}
              <Dialog.Root open={open} onOpenChange={setOpen} modal={false}>
                <Dialog.Trigger
                  className={className}
                  onClick={() => {}}
                  title={'...'}
                >
                  <ChatBubbleOvalLeftEllipsisIcon
                    className="w-5 h-5"
                    aria-hidden={true}
                  />
                </Dialog.Trigger>
                <Dialog.Portal container={container}>
                  <Dialog.Content
                    className="bg-white shadow-lg sm:rounded-lg absolute top-12 right-0 max-w-[400px] w-full dark:bg-gray-600"
                    onPointerDownOutside={(event) => {
                      event.preventDefault();
                    }}
                  >
                    <div className="px-4 py-5 sm:p-6 grid gap-y-6">
                      <CreateNewObjectForm onSubmit={handleCreateNewObject} type={type} />
                      {!type && (
                        <>
                          <GenerateObjectForm
                            dataClassUuid={dataClassUuids[0]}
                            schema={schema}
                            onSuccess={handleGenerateSuccess}
                          />
                          <RefineForm
                            nodeFormData={nodeFormData}
                            onSuccess={handleRefineSuccess}
                          />
                        </>
                      )}
                    </div>
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog.Root>
            </div>
            {!deleted && enabled && schema && !syncing && nodeFormData ? (
              <EditForm
                nodeFormData={nodeFormData}
                schema={schema}
                uiSchema={uiSchema}
                onSuccess={handleUpsertSuccess}
                uuid={uuids[0]}
                dataClassUuid={dataClassUuids[0]}
                container={containers[0]}
                onUpsertStart={handleUpsertStart}
                type={type}
              >
                {(isLoading, upsertError) => {
                  return (
                    <>
                      {chains && chains.length > 0 ? (
                        <div className="mt-6 border-y-2 border-gray-200 dark:border-gray-700 py-6">
                          <p className="block text-md font-bold text-black dark:text-slate-300 mb-1">Trigger Chain</p>
                          <ChainTriggerForm
                            chains={chains}
                            headers={headers}
                            nodeFormData={nodeFormData}
                          />
                        </div>
                      ) : null}
                      {upsertError && (
                        <div className="rounded-md bg-red-50 p-4 mt-4 mb-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <XCircleIcon
                                className="h-5 w-5 text-red-400"
                                aria-hidden="true"
                              />
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-red-800">
                                Save Failed
                              </h3>
                              <div className="mt-2 text-sm text-red-700">
                                <p>{upsertError}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {objectSaved && (
                        <div className="rounded-md bg-green-50 p-4 mt-4 mb-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <CheckCircleIcon
                                className="h-5 w-5 text-green-400"
                                aria-hidden="true"
                              />
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-green-800">
                                Object Saved
                              </h3>
                              <div className="mt-2 text-sm text-green-700">
                                <p>The object has been succesfully saved.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2.5 mt-4">
                        {dataClassUuids?.[0] === "0db17962-69a2-4716-ac68-4c48c45637d0" && (
                          <button
                            onClick={handleDownloadClick}
                            type="button"
                            className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          >
                            <ArrowDownTrayIcon aria-hidden="true" className="-ml-0.5 mr-1.5 w-4 h-4 mt-0.5" />
                            Download
                          </button>
                        )}
                        <button
                          type="submit"
                          className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                          {isLoading ? (
                            <>
                              <svg
                                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            <>Submit</>
                          )}
                        </button>
                        {!create && (
                          <DeleteObject
                            uuid={uuids[0]}
                            container={containers[0]}
                            type={type}
                            onSuccess={handleDeleteSuccess}
                          />
                        )}
                      </div>
                    </>
                  );
                }}
              </EditForm>
            ) : null}
            {!deleted && syncing && (
              <div className="flex items-center justify-center h-[500px]">
                <div className="px-4 py-2 text-xs font-medium leading-none text-center text-blue-800 bg-blue-200 rounded-full animate-pulse dark:bg-blue-900 dark:text-blue-200">Syncing, please wait...</div>
              </div>
            )}
          </PanelProvider>
        </div>
    </Widget>
  );
}
